'use client';

import {
  type AdminInfo,
  type ApiResponse,
  type GrantIntegralParams,
  type SetCodeParams,
  type SetImageCountParams,
  type SetWorkflowCountParams,
  adminLogin,
  changeAdminPassword,
  getCurrentAdmin,
  grantIntegral,
  setCode,
  setImageCount,
  setWorkflowCount,
} from '@/client/api/admin';
import sdk from '@/client/api/sdk';
import { useAsyncEffect } from 'ahooks';
import { useRouter } from 'next/navigation';
import React, { createContext, useContext, useState } from 'react';

interface AdminAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  changePassword: (
    oldPassword: string,
    newPassword: string,
  ) => Promise<{
    success: boolean;
    message?: string;
  }>;
  getCurrentAdmin: () => Promise<AdminInfo | null>;
  grantIntegral: (params: GrantIntegralParams) => Promise<ApiResponse>;
  setCode: (params: SetCodeParams) => Promise<ApiResponse>;
  setImageCount: (params: SetImageCountParams) => Promise<ApiResponse>;
  setWorkflowCount: (params: SetWorkflowCountParams) => Promise<ApiResponse>;
  adminName: string | null;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminName, setAdminName] = useState<string | null>(null);
  const router = useRouter();

  // 智能认证检查 - 检查本地存储并验证 token 有效性
  useAsyncEffect(async () => {
    const token = localStorage.getItem('adminToken');

    if (!token) {
      router.push('/login');
      setIsLoading(false);
      return;
    }

    // 先设置为已认证状态（基于本地存储）
    setIsAuthenticated(true);
    setAdminName(localStorage.getItem('adminName') || null);
    setIsLoading(false);
  }, []);

  const login = async (name: string, password: string): Promise<boolean> => {
    try {
      const result = await adminLogin(name, password);

      if (result.success && result.data) {
        setIsAuthenticated(true);
        setAdminName(result.data.adminName);
        setIsLoading(false);

        return true;
      }

      return false;
    } catch (_error) {
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    // 避免重复调用逻辑：如果没有token，直接返回
    if (!localStorage.getItem('adminToken')) {
      return;
    }

    try {
      // 调用登出API，SDK拦截器会自动添加正确的Authorization头
      await sdk.POST('/api/admin/logout');
    } catch (_error) {
      // Handle error silently
    } finally {
      // Clear auth data regardless of API response
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminName');
      localStorage.removeItem('adminId');

      setIsAuthenticated(false);
      setAdminName(null);

      router.push('/login');
    }
  };

  const changePassword = async (
    oldPassword: string,
    newPassword: string,
  ): Promise<{ success: boolean; message?: string }> => {
    return await changeAdminPassword(oldPassword, newPassword);
  };

  const getCurrentAdminInfo = async (): Promise<AdminInfo | null> => {
    const adminInfo = await getCurrentAdmin();

    // 更新本地存储的管理员名称
    if (adminInfo?.adminName && adminInfo.adminName !== localStorage.getItem('adminName')) {
      localStorage.setItem('adminName', adminInfo.adminName);
      setAdminName(adminInfo.adminName);
    }

    return adminInfo;
  };

  // 积分赠送方法
  const grantIntegralMethod = async (params: GrantIntegralParams): Promise<ApiResponse> => {
    return await grantIntegral(params);
  };

  // 设置验证码方法
  const setCodeMethod = async (params: SetCodeParams): Promise<ApiResponse> => {
    return await setCode(params);
  };

  // 设置图片计数方法
  const setImageCountMethod = async (params: SetImageCountParams): Promise<ApiResponse> => {
    return await setImageCount(params);
  };

  // 设置工作流计数方法
  const setWorkflowCountMethod = async (params: SetWorkflowCountParams): Promise<ApiResponse> => {
    return await setWorkflowCount(params);
  };

  return (
    <AdminAuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        login,
        logout,
        changePassword,
        getCurrentAdmin: getCurrentAdminInfo,
        grantIntegral: grantIntegralMethod,
        setCode: setCodeMethod,
        setImageCount: setImageCountMethod,
        setWorkflowCount: setWorkflowCountMethod,
        adminName,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
